import React, { useEffect, useState } from 'react'
import { Button, Flex, Modal, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useUnit } from 'effector-react'
import { $pendingGatewayId, openBlockModal } from '../blockGateway'
import { ALL_GATEWAYS_PAGE_SIZE } from '../const'
import { FilterSearchInput } from '../filters'
import { useAllGateways } from './hooks'
import {
  $isAllGatewaysOpen,
  allGatewaysLoad,
  closeAllGatewaysModal,
  openAllGatewaysModal,
} from './model'
import { SystemGateway } from './types'

const MODAL_WIDTH = 640

const AllGatewaysTable: React.FC = () => {
  const { gateways, total, loading, blockedGatewayIds } = useAllGateways()
  const pendingGatewayId = useUnit($pendingGatewayId)

  const [gatewayId, setGatewayId] = useState('')
  const [internalName, setInternalName] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(ALL_GATEWAYS_PAGE_SIZE)

  useEffect(() => {
    allGatewaysLoad({ gatewayId, internalName, page, pageSize })
  }, [gatewayId, internalName, page, pageSize])

  const applyGatewayId = (value: string) => {
    setGatewayId(value)
    setPage(1)
  }

  const applyInternalName = (value: string) => {
    setInternalName(value)
    setPage(1)
  }

  const columns: ColumnsType<SystemGateway> = [
    {
      title: 'Gateway ID',
      dataIndex: 'gatewayId',
      width: 110,
    },
    {
      title: 'Internal Name',
      dataIndex: 'internalName',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, gateway: SystemGateway) => {
        if (blockedGatewayIds.has(gateway.gatewayId)) {
          return (
            <Tag color="red" style={{ fontSize: 10, fontWeight: 700 }}>
              Blocked
            </Tag>
          )
        }

        return (
          <Button
            size="small"
            danger
            loading={pendingGatewayId === gateway.gatewayId}
            onClick={() =>
              openBlockModal({
                gatewayId: gateway.gatewayId,
                internalName: gateway.internalName,
              })
            }
          >
            Block
          </Button>
        )
      },
    },
  ]

  return (
    <Flex vertical gap={12}>
      <Flex gap={6} wrap="wrap">
        <FilterSearchInput
          placeholder="Gateway ID"
          value={gatewayId}
          onApply={applyGatewayId}
          width={160}
          numericOnly
        />
        <FilterSearchInput
          placeholder="Internal Name"
          value={internalName}
          onApply={applyInternalName}
          width={260}
        />
      </Flex>

      <Table
        dataSource={gateways}
        loading={loading}
        rowKey="gatewayId"
        columns={columns}
        size="small"
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          total,
          position: ['bottomCenter'],
        }}
        rowClassName={(_, index) => `${index % 2 ? 'even-row' : 'odd-row'}`}
        onChange={(pagination) => {
          setPage(pagination.current ?? 1)
          setPageSize(pagination.pageSize ?? ALL_GATEWAYS_PAGE_SIZE)
        }}
      />
    </Flex>
  )
}

export const BlockAnyGateway: React.FC = () => {
  const isOpen = useUnit($isAllGatewaysOpen)

  return (
    <>
      <Button danger onClick={() => openAllGatewaysModal()}>
        Block any gateway
      </Button>

      {/* The reason modal is rendered by the list and opens on top of this
          one: both are destroyed on close, so the one opened last is mounted
          last. */}
      <Modal
        title="Block any gateway"
        open={isOpen}
        onCancel={() => closeAllGatewaysModal()}
        footer={null}
        width={MODAL_WIDTH}
        destroyOnHidden
      >
        <AllGatewaysTable />
      </Modal>
    </>
  )
}
